// Analytics Event Tracking Layer
// Multi-cert: analytics events are cert-scoped via certPath()
// Buffer is global but events carry certId for cert-scoped writes

import { certPath, push, read } from './firebase-store.js';

// In-memory buffer — batch writes to reduce Firebase cost
let _buffer = [];
let _flushTimer = null;
const FLUSH_INTERVAL_MS = 10000;
const BUFFER_MAX = 50;

/**
 * Track a generic analytics event (cert-scoped).
 *
 * @param {string} userId
 * @param {string} certId
 * @param {string} eventType
 * @param {object} data - event-specific payload
 */
export function trackEvent(userId, certId, eventType, data = {}) {
  const event = {
    type: eventType,
    ts: new Date().toISOString(),
    ...data
  };

  _buffer.push({ userId, certId, event });

  if (_buffer.length >= BUFFER_MAX) {
    _flushBuffer();
  }

  if (!_flushTimer) {
    _flushTimer = setTimeout(_flushBuffer, FLUSH_INTERVAL_MS);
  }
}

/**
 * Track question shown event (cert-scoped).
 * @param {string} userId
 * @param {string} certId
 * @param {object} opts
 */
export function trackQuestionShown(userId, certId, opts) {
  trackEvent(userId, certId, 'question_shown', {
    questionId: opts.questionId,
    sessionId: opts.sessionId,
    phase: opts.phase || null,
    reasonCode: opts.reasonCode || null,
    position: opts.position || 0
  });
}

/**
 * Track answer with distractor info (cert-scoped).
 * @param {string} userId
 * @param {string} certId
 * @param {object} opts
 */
export function trackAnswer(userId, certId, opts) {
  trackEvent(userId, certId, 'answer_submitted', {
    questionId: opts.questionId,
    sessionId: opts.sessionId,
    correct: opts.correct,
    responseMs: opts.responseMs || 0,
    selectedChoice: opts.selectedChoice || null,
    correctChoice: opts.correctChoice || null,
    distractorChosen: opts.distractorChosen || null,
    confidence: opts.confidence || 'unsure'
  });
}

/**
 * Track reinforcement recovery (cert-scoped).
 * @param {string} userId
 * @param {string} certId
 * @param {object} opts
 */
export function trackReinforcement(userId, certId, opts) {
  trackEvent(userId, certId, 'reinforcement_result', {
    questionId: opts.questionId,
    relatedToQuestionId: opts.relatedToQuestionId || null,
    sessionId: opts.sessionId || null,
    conceptTag: opts.conceptTag || null,
    recovered: opts.recovered || false
  });
}

/**
 * Track session lifecycle (cert-scoped).
 * @param {string} userId
 * @param {string} certId
 * @param {'start'|'end'} action
 * @param {object} data
 */
export function trackSession(userId, certId, action, data = {}) {
  trackEvent(userId, certId, action === 'start' ? 'session_start' : 'session_end', data);
}

/**
 * Flush buffered events to Firebase.
 * Groups by userId+certId for cert-scoped writes.
 */
async function _flushBuffer() {
  if (_flushTimer) {
    clearTimeout(_flushTimer);
    _flushTimer = null;
  }

  if (_buffer.length === 0) return;

  const batch = _buffer.splice(0);

  // Group by userId+certId
  const byUserCert = {};
  for (const { userId, certId, event } of batch) {
    const key = userId + '::' + certId;
    if (!byUserCert[key]) byUserCert[key] = { userId, certId, events: [] };
    byUserCert[key].events.push(event);
  }

  // Write each user+cert's events
  for (const { userId, certId, events } of Object.values(byUserCert)) {
    for (const event of events) {
      try {
        await push(certPath(userId, certId, 'analytics'), event);
      } catch (e) {
        // Firebase not available — discard silently
      }
    }
  }
}

/**
 * Force flush (call on session end or app close).
 */
export async function flushAnalytics() {
  await _flushBuffer();
}

// ── Query Functions ──

/**
 * Get distractor frequency (cert-scoped).
 * @param {string} userId
 * @param {string} certId
 */
export async function getDistractorFrequency(userId, certId) {
  let analytics;
  try {
    analytics = await read(certPath(userId, certId, 'analytics'));
  } catch (e) { return {}; }
  if (!analytics) return {};

  const freq = {};
  for (const event of Object.values(analytics)) {
    if (event.type !== 'answer_submitted' || event.correct || !event.distractorChosen) continue;
    const qid = event.questionId;
    if (!freq[qid]) freq[qid] = {};
    freq[qid][event.distractorChosen] = (freq[qid][event.distractorChosen] || 0) + 1;
  }
  return freq;
}

/**
 * Get reinforcement recovery rate (cert-scoped).
 * @param {string} userId
 * @param {string} certId
 */
export async function getReinforcementRecoveryRate(userId, certId) {
  let analytics;
  try {
    analytics = await read(certPath(userId, certId, 'analytics'));
  } catch (e) { return { attempts: 0, recovered: 0, rate: 0 }; }
  if (!analytics) return { attempts: 0, recovered: 0, rate: 0 };

  let attempts = 0;
  let recovered = 0;
  for (const event of Object.values(analytics)) {
    if (event.type !== 'reinforcement_result') continue;
    attempts++;
    if (event.recovered) recovered++;
  }

  return {
    attempts,
    recovered,
    rate: attempts > 0 ? Math.round(recovered / attempts * 100) / 100 : 0
  };
}

/**
 * Get session analytics summary (cert-scoped).
 * @param {string} userId
 * @param {string} certId
 * @param {string} sessionId
 */
export async function getSessionAnalytics(userId, certId, sessionId) {
  let analytics;
  try {
    analytics = await read(certPath(userId, certId, 'analytics'));
  } catch (e) { return null; }
  if (!analytics) return null;

  const sessionEvents = Object.values(analytics)
    .filter(e => e.sessionId === sessionId);

  const answers = sessionEvents.filter(e => e.type === 'answer_submitted');
  const shown = sessionEvents.filter(e => e.type === 'question_shown');

  const responseTimes = answers.filter(a => a.responseMs > 0).map(a => a.responseMs);
  const avgResponseMs = responseTimes.length > 0
    ? Math.round(responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length)
    : 0;

  const accuracyByPosition = [];
  for (let i = 0; i < answers.length; i++) {
    const window = answers.slice(Math.max(0, i - 4), i + 1);
    const correct = window.filter(a => a.correct).length;
    accuracyByPosition.push(Math.round(correct / window.length * 100) / 100);
  }

  return {
    questionsShown: shown.length,
    questionsAnswered: answers.length,
    avgResponseMs,
    accuracyByPosition
  };
}
